import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import moment from 'moment';

export interface DirectoryUploadItem {
  directoryName: string;
  uploadDate: string;
  files: File[];
  displayDate: string;
}

/** uploadedResumes counts files sent to the server; processedResumes comes from BULK_UPLOAD_PROGRESS events. */
export interface UploadProgress {
  bulkUploadCorrelationId: string;
  uploadSessionId: string;
  totalResumes: number;
  uploadedResumes: number;
  processedResumes: number;
  progressPercentage: number;
  status: 'IDLE' | 'UPLOADING' | 'PROCESSING' | 'COMPLETED';
  isProcessing: boolean;
  message: string;
}

export interface FailedFile {
  fileName: string;
  directoryName: string;
  error: string;
  reason: string;
}

/** Aligned with BULK_UPLOAD_COMPLETED WebSocket event data */
export interface BulkUploadSummary {
  directoryName: string;
  uploadDate: string;
  totalFiles: number;
  successfulFiles: number;
  failedFiles: number;
  uploadedAt: string;
  uploadedBy: string;
  bulkUploadCorrelationId: string;
  uploadSessionId: string;
  failedFilesList: FailedFile[];
  candidates: ProcessedCandidate[];
  message: string;
  jobId: string;
}

export interface ProcessedCandidate {
  candidateId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  score: number;
  newCandidate: boolean;
}

/** Backend 202 Accepted response shape */
export interface BatchUploadResponse {
  status: string;
  message: string;
  uploadSessionId: string;
  totalFiles: number;
  totalFilesReceived: number;
  filesCount: number;
  totalBatches: number;
  estimatedDuration: string;
}

interface ActiveUpload {
  correlationId: string;
  directoryName: string;
  uploadDate: string;
  userId: string;
  jobId: string;
}

@Injectable({
  providedIn: 'root'
})
export class BulkResumeUploadService {

  /** Number of files sent per HTTP request to the backend */
  private readonly UI_BATCH_SIZE = 20;
  private readonly BULK_UPLOAD_URL = '/api/resume/bulk-upload';

  private progress: UploadProgress = this.getInitialProgress();
  private progressSubject = new Subject<UploadProgress>();
  public progress$ = this.progressSubject.asObservable();

  private summarySubject = new Subject<BulkUploadSummary>();
  public summary$ = this.summarySubject.asObservable();

  private uploadCompleteSubject = new Subject<void>();
  public uploadComplete$ = this.uploadCompleteSubject.asObservable();

  private summaryClosedSubject = new Subject<BulkUploadSummary>();
  /** Emits the summary once the user has closed its modal (including to retry the failed files). */
  public summaryClosed$ = this.summaryClosedSubject.asObservable();

  private latestSummary: BulkUploadSummary = null;
  private activeUpload: ActiveUpload = null;

  constructor(private httpClient: HttpClient) { }

  private setProgress(next: UploadProgress): void {
    this.progress = next;
    this.progressSubject.next(next);
  }

  get currentProgress(): UploadProgress {
    return this.progress;
  }

  /** Summary of the most recent completed upload, kept until the user has closed it. */
  get completedSummary(): BulkUploadSummary {
    return this.latestSummary;
  }

  /**
   * Feed in a BULK_UPLOAD_PROGRESS WebSocket event payload. Only the counts change; events for
   * another upload, or older than the counts already shown, are ignored.
   */
  handleProgressEvent(data: any, message?: string): void {
    const upload = this.activeUpload;
    const current = this.progress;
    if (!upload || (data.bulkUploadCorrelationId && data.bulkUploadCorrelationId !== upload.correlationId)) { return; }
    if (data.processedResumes < current.processedResumes) { return; }
    const total = data.totalResumes || current.totalResumes;
    this.setProgress({
      ...current,
      totalResumes: total,
      processedResumes: data.processedResumes,
      progressPercentage: data.progressPercentage != null
        ? data.progressPercentage
        : (total ? Math.round((data.processedResumes / total) * 100) : 0),
      status: 'PROCESSING',
      message: message || current.message
    });
  }

  /**
   * Feed in a BULK_UPLOAD_COMPLETED WebSocket event payload to finalize progress
   * and emit the upload summary. Events that do not belong to the running upload are ignored.
   */
  handleCompletedEvent(data: any, message?: string): void {
    const upload = this.activeUpload;
    if (!upload || (data.bulkUploadCorrelationId && data.bulkUploadCorrelationId !== upload.correlationId)) { return; }
    this.activeUpload = null;

    this.setProgress({
      ...this.progress,
      uploadedResumes: data.totalResumes,
      processedResumes: data.totalResumes,
      progressPercentage: 100,
      status: 'COMPLETED',
      isProcessing: false,
      message: message || 'Upload complete'
    });

    this.latestSummary = {
      directoryName: upload.directoryName,
      uploadDate: upload.uploadDate,
      totalFiles: data.totalResumes,
      successfulFiles: data.successResumes,
      failedFiles: data.failedResumes,
      uploadedAt: moment().format('MMM DD, YYYY hh:mm A'),
      uploadedBy: upload.userId,
      bulkUploadCorrelationId: data.bulkUploadCorrelationId || upload.correlationId,
      uploadSessionId: data.uploadSessionId,
      failedFilesList: this.getFailedFiles(data),
      candidates: Array.isArray(data.candidates) ? data.candidates : [],
      message: message || '',
      jobId: upload.jobId
    };

    this.summarySubject.next(this.latestSummary);
    this.uploadCompleteSubject.next();
  }

  private getFailedFiles(data: any): FailedFile[] {
    if (Array.isArray(data.failedFileList)) { return data.failedFileList; }
    if (Array.isArray(data.failedFiles)) { return data.failedFiles; }
    const unprocessed = Array.isArray(data.unProcessedCandidates) ? data.unProcessedCandidates : [];
    return unprocessed.map(c => ({ fileName: c.failedFileName, directoryName: '', error: '', reason: c.failureReason }));
  }

  /** The user has dealt with the completed upload, so the snackbar can go away. */
  closeSummary(): void {
    const summary = this.latestSummary;
    this.latestSummary = null;
    this.resetProgress();
    this.summaryClosedSubject.next(summary);
  }

  /**
   * Main bulk upload: groups all files into batches of 20 and POSTs each batch
   * to POST /resume/bulk-upload (202 Accepted). Progress and the result arrive
   * later via the WebSocket BULK_UPLOAD_PROGRESS / BULK_UPLOAD_COMPLETED events.
   *
   * Returns the bulkUploadCorrelationId so the caller can correlate WS events.
   */
  async processBulkUpload(
    directories: DirectoryUploadItem[],
    sourceTypeId: string,
    userId: string,
    companyId: string,
    folderIds: string,
    sourceTypeDetails?: any,
    jobId?: string
  ): Promise<string> {
    const bulkUploadCorrelationId = this.generateUUID();

    // Total across the whole session (all selected directories combined).
    const filesCount = directories.reduce((sum, dir) => sum + dir.files.length, 0);

    this.latestSummary = null;
    this.activeUpload = {
      correlationId: bulkUploadCorrelationId,
      directoryName: directories.map(dir => dir.directoryName).join(', '),
      uploadDate: sourceTypeDetails ? sourceTypeDetails.uploadDate : '',
      userId,
      jobId: jobId || ''
    };

    this.setProgress({
      ...this.getInitialProgress(),
      bulkUploadCorrelationId,
      totalResumes: filesCount,
      status: 'UPLOADING',
      isProcessing: true,
      message: 'Uploading files to server...'
    });

    try {
      // Batch strictly within each directory's own files so a chunk never mixes
      // files from two different directories under one directoryName.
      let filesSentSoFar = 0;
      for (const dir of directories) {
        const dirBatchCount = Math.ceil(dir.files.length / this.UI_BATCH_SIZE);

        for (let batchNum = 0; batchNum < dirBatchCount; batchNum++) {
          const start = batchNum * this.UI_BATCH_SIZE;
          const end = Math.min(start + this.UI_BATCH_SIZE, dir.files.length);
          const batchFiles = dir.files.slice(start, end);
          const fileRange = `${filesSentSoFar + 1}-${filesSentSoFar + batchFiles.length}`;

          const formData = new FormData();
          batchFiles.forEach(file => formData.append('files', file));
          formData.append('bulkUploadCorrelationId', bulkUploadCorrelationId);
          formData.append('directoryName', dir.directoryName);
          formData.append('fileRange', fileRange);
          formData.append('filesCount', String(filesCount));
          formData.append('companyId', companyId);
          formData.append('userId', userId);
          if (folderIds) { formData.append('folderIds', folderIds); }
          if (jobId) { formData.append('jobId', jobId); }
          if (sourceTypeId) { formData.append('resumeSourceTypeId', sourceTypeId); }
          if (sourceTypeDetails) {
            if (sourceTypeDetails.otherSourceTypeNotes) {
              formData.append('otherSourceTypeNotes', sourceTypeDetails.otherSourceTypeNotes);
            }
            if (sourceTypeDetails.referrerName) {
              formData.append('referrerName', sourceTypeDetails.referrerName);
            }
            if (sourceTypeDetails.referrerEmail) {
              formData.append('referrerEmail', sourceTypeDetails.referrerEmail);
            }
            if (sourceTypeDetails.uploadDate) {
              formData.append('uploadDateAndTime', sourceTypeDetails.uploadDate);
            }
          }

          const response = await this.httpClient.post<BatchUploadResponse>(this.BULK_UPLOAD_URL, formData).toPromise();
          filesSentSoFar += batchFiles.length;
          this.reportUploaded(filesSentSoFar, filesCount, response && response.uploadSessionId);
        }
      }
    } catch (error) {
      console.error('Bulk upload batch error:', error);
      this.activeUpload = null;
      this.setProgress({
        ...this.progress,
        status: 'IDLE',
        isProcessing: false,
        message: `Upload error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      throw error;
    }

    return bulkUploadCorrelationId;
  }

  resetProgress(): void {
    this.setProgress(this.getInitialProgress());
  }

  private reportUploaded(uploaded: number, total: number, uploadSessionId: string): void {
    const current = this.progress;
    if (current.status === 'COMPLETED') { return; }
    this.setProgress({
      ...current,
      uploadSessionId: uploadSessionId || current.uploadSessionId,
      uploadedResumes: uploaded,
      status: uploaded >= total || current.processedResumes > 0 ? 'PROCESSING' : 'UPLOADING'
    });
  }

  private getInitialProgress(): UploadProgress {
    return {
      bulkUploadCorrelationId: '',
      uploadSessionId: '',
      totalResumes: 0,
      uploadedResumes: 0,
      processedResumes: 0,
      progressPercentage: 0,
      status: 'IDLE',
      isProcessing: false,
      message: 'Ready to upload'
    };
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
